import * as Popover from '@radix-ui/react-popover'
import { Link, useNavigate } from '@tanstack/react-router'
import { CalendarX, ChevronDown, ChevronUp, MessageSquare, MoreHorizontal, Pause, Pencil, Play, Plus, Repeat, Sparkles, X } from 'lucide-react'
import { useId, useState } from 'react'
import { Card, CardHeader } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { ErrorState } from '@/components/ui/ErrorState'
import { Modal } from '@/components/ui/Modal'
import { PageHeader } from '@/components/ui/PageHeader'
import { PersonScopeControl } from '@/components/scope/PersonScopeControl'
import { SearchSelect } from '@/components/ui/SearchSelect'
import { Select } from '@/components/ui/Select'
import { Skeleton } from '@/components/ui/Skeleton'
import { Tooltip } from '@/components/ui/Tooltip'
import { useIsAdmin } from '@/lib/auth'
import { toNumber } from '@/lib/decimal'
import { formatCompactEuros, formatEuros, formatMonthYear } from '@/lib/format'
import { useInvoiceClients } from '@/lib/invoice-queries'
import { useMonthSelection } from '@/lib/use-month-selection'
import { useCreateRecurring, useRecurringActions, useRecurringOverview, useRecurringPersonalForecast } from '@/lib/recurring-api'
import type { ForecastLine, ForecastPoint, RecurringFrequency, RecurringListItem } from '@/lib/recurring-types'
import type { ViewScope } from '@/lib/report-types'
import { cn } from '@/lib/utils'

const FREQ_LABEL: Record<RecurringFrequency, string> = {
  ONCE: 'Eenmalig',
  MONTHLY: 'Maandelijks',
  QUARTERLY: 'Per kwartaal',
  YEARLY: 'Jaarlijks',
}
const FREQ_OPTIONS: RecurringFrequency[] = ['ONCE', 'MONTHLY', 'QUARTERLY', 'YEARLY']

interface ClientBucket {
  clientName: string
  brandColor: string
  templates: RecurringListItem[]
}

/**
 * Reeksen staan per klant bij elkaar: een klant met meerdere uitgiftedata heeft
 * meerdere reeksen, en die horen visueel bij elkaar te staan.
 */
function byClient(templates: RecurringListItem[]): ClientBucket[] {
  const buckets = new Map<string, ClientBucket>()
  for (const template of templates) {
    const bucket = buckets.get(template.clientName)
      ?? { clientName: template.clientName, brandColor: template.brandColor, templates: [] }
    bucket.templates.push(template)
    buckets.set(template.clientName, bucket)
  }
  return [...buckets.values()]
    .map((bucket) => ({
      ...bucket,
      templates: [...bucket.templates].sort((a, b) => (a.nextIssueDate ?? '').localeCompare(b.nextIssueDate ?? '')),
    }))
    .sort((a, b) => a.clientName.localeCompare(b.clientName))
}

export function RecurringPage() {
  const isAdmin = useIsAdmin()
  const overviewQuery = useRecurringOverview()
  const [createOpen, setCreateOpen] = useState(false)

  const templates = overviewQuery.data?.templates ?? []
  const buckets = byClient(templates)

  return (
    <div className="space-y-6">
      <PageHeader
        title="Terugkerende facturen"
        description="Reeksen per klant, elk met een eigen schema, groepen en verdeling."
        actions={
          isAdmin ? (
            <button type="button" className="btn-primary" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> Nieuwe reeks
            </button>
          ) : null
        }
      />

      {overviewQuery.isError ? (
        <ErrorState error={overviewQuery.error} onRetry={() => overviewQuery.refetch()} />
      ) : overviewQuery.isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full rounded-card" />
          ))}
        </div>
      ) : buckets.length === 0 ? (
        <EmptyState
          icon={Repeat}
          title="Nog geen terugkerende reeksen"
          description="Voeg een reeks toe om automatisch concepten te laten genereren."
          action={
            isAdmin ? (
              <button type="button" className="btn-primary" onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4" /> Nieuwe reeks
              </button>
            ) : null
          }
        />
      ) : (
        <div className="space-y-4">
          {buckets.map((bucket) => (
            <ClientCard key={bucket.clientName} bucket={bucket} />
          ))}
        </div>
      )}

      <ForecastCard companyForecast={overviewQuery.data?.forecast ?? []} loadingCompany={overviewQuery.isLoading} />

      {createOpen ? <CreateRecurringModal onClose={() => setCreateOpen(false)} /> : null}
    </div>
  )
}

/**
 * De prognose staat standaard op het eigen aandeel; bedrijfsbreed is een klik
 * verder, net als op het dashboard. Het eigen deel wordt pas opgehaald zodra de
 * kaart in beeld komt, want daarvoor moet per reeks de verdeling worden bepaald.
 * Personenkeuze is bewust lokale kaart-state (geen URL): dezelfde schakelaar als
 * op het dashboard, maar hier reset hij bij het verlaten van de pagina.
 */
function ForecastCard({ companyForecast, loadingCompany }: { companyForecast: ForecastPoint[]; loadingCompany: boolean }) {
  const [scope, setScope] = useState<ViewScope>('personal')
  const [personId, setPersonId] = useState<string | undefined>(undefined)
  const personalQuery = useRecurringPersonalForecast(12, personId)

  const people = personalQuery.data?.people ?? []
  const self = people.find((person) => person.isSelf)
  const viewingSelf = personId === undefined || personId === self?.id
  const selectedPerson = viewingSelf ? undefined : people.find((person) => person.id === personId)

  const personal = scope === 'personal'
  const points = personal ? (personalQuery.data?.forecast ?? []) : companyForecast
  const loading = personal ? personalQuery.isLoading : loadingCompany
  const max = Math.max(1, ...points.map((p) => toNumber(p.amount)))
  const unattributed = personalQuery.data?.unattributed
  // Alleen tijdens het laden vullen we aan met placeholders; anders zou een echte lege
  // forecast (niemand, of deze persoon deelt nergens in) blijvend skeletons tonen in
  // plaats van de "geen data"-melding eronder.
  const columns = loading ? Array.from({ length: 12 }).map(() => null) : points

  // Het paneel staat hier altijd onder de grafiek en niet naast de kaart ernaast: de
  // Forecast-kaart loopt over de volle breedte en heeft geen buur om over te nemen.
  const { selectedMonth, selectedPoint, selectMonth, panelRef, expanded, setExpanded } = useMonthSelection(points, {
    panelAlwaysBelow: true,
  })

  function selectPerson(next: string | undefined) {
    setScope('personal')
    setPersonId(next)
  }
  const aandeel = personal
    ? viewingSelf
      ? 'Jouw aandeel in de verwachte concepten'
      : `Aandeel van ${selectedPerson?.displayName ?? ''} in de verwachte concepten`
    : 'Verwachte concepten komende 12 maanden'
  // Dat je kunt kiezen staat in de subtitle en verdwijnt zodra je het doet.
  const subtitle = selectedMonth === null && points.length > 0 ? `${aandeel} · kies een maand` : aandeel

  return (
    <Card>
      <CardHeader
        title="Forecast"
        subtitle={subtitle}
        action={
          <PersonScopeControl
            scope={scope}
            people={people}
            personId={personId}
            onScope={setScope}
            onPerson={selectPerson}
            personalLabel="Mijn deel"
            companyLabel="Bedrijf"
            menuTitle="Bekijk de prognose van"
            label="Weergave"
          />
        }
      />
      <div className="mt-6">
        {/* De basislijn ligt achter de kolommen in plaats van eronder: het maandlabel hoort
            binnen het klikdoel, want op 375px is een kolom nog geen twintig pixel breed. */}
        <div className="relative">
          <div className="pointer-events-none absolute inset-x-0 top-0 border-b border-hairline" style={{ height: BAR_AREA }} />
          <div className="relative flex gap-1.5">
            {columns.map((point, i) => (
              <ForecastColumn
                key={point ? point.month : i}
                point={point}
                index={i}
                max={max}
                selected={point !== null && point.month === selectedMonth}
                dimmed={selectedMonth !== null && point?.month !== selectedMonth}
                onSelect={selectMonth}
              />
            ))}
          </div>
        </div>
        {selectedPoint ? (
          <div ref={panelRef} className="mt-5 border-t border-hairline pt-4">
            <MonthLines
              key={selectedPoint.month}
              point={selectedPoint}
              personal={personal}
              expanded={expanded}
              onExpandedChange={setExpanded}
              onClose={() => selectMonth(null)}
            />
          </div>
        ) : null}
        {personal && !loading && points.length === 0 ? (
          <p className="mt-4 text-sm text-ink-faint">Geen van de reeksen komt bij jou terecht.</p>
        ) : null}
        {personal && unattributed && toNumber(unattributed.amount) > 0 ? (
          <p className="mt-4 text-xs text-ink-faint">
            <span className="money">{formatEuros(toNumber(unattributed.amount))}</span> is nog niet toe te rekenen:
            de verdeling van {unattributed.templates.join(', ')} is niet te bepalen.
          </p>
        ) : null}
      </div>
    </Card>
  )
}

/** Ruimte voor het bedraglabel plus de hoogste staaf; ook de hoogte van de basislijn. */
const BAR_MAX = 112
const BAR_AREA = BAR_MAX + 20

/**
 * Een maand in de prognose. De hele kolom is de schakelaar naar de opbouw van die maand;
 * nogmaals klikken zet hem weer uit. Alles binnen de knop is een span: een div mag daar
 * niet in staan.
 */
function ForecastColumn({
  point,
  index,
  max,
  selected,
  dimmed,
  onSelect,
}: {
  point: ForecastPoint | null
  index: number
  max: number
  selected: boolean
  dimmed: boolean
  onSelect: (month: string | null) => void
}) {
  if (!point) {
    return (
      <span className="flex flex-1 flex-col items-center">
        <span className="flex w-full items-end justify-center" style={{ height: BAR_AREA }}>
          <Skeleton className="h-16 w-6 rounded-t-[2px]" />
        </span>
        <span className="mt-2 h-[13px]" />
      </span>
    )
  }

  const amount = toNumber(point.amount)
  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-label={`Opbouw van ${monthLabel(point.month)}, ${formatEuros(amount)}`}
      onClick={() => onSelect(selected ? null : point.month)}
      className={cn(
        'group flex flex-1 flex-col items-center rounded-md transition-colors duration-150 ease-spring',
        'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-500/40',
        selected ? 'bg-fill-2' : 'hover:bg-fill-1',
      )}
    >
      <span
        className={cn('flex w-full flex-col items-center justify-end transition-opacity duration-150 ease-spring', dimmed && 'opacity-60 group-hover:opacity-100')}
        style={{ height: BAR_AREA }}
      >
        <span className="money mb-1 text-[10px] leading-none text-ink-faint">{formatCompactEuros(amount)}</span>
        <span
          className="w-6 max-w-[24px] origin-bottom animate-grow-bar rounded-t-[2px] bg-accent-600/70"
          style={{ height: `${Math.max(3, Math.round((amount / max) * BAR_MAX))}px`, animationDelay: `${index * 30}ms` }}
        />
      </span>
      <span className={cn('mt-2 text-[10px] leading-none', selected ? 'font-medium text-ink-soft' : 'text-ink-faint')}>
        {point.month.slice(5)}
      </span>
    </button>
  )
}

/** "2026-09" -> "September 2026". */
function monthLabel(month: string): string {
  const [year, index] = month.split('-')
  return formatMonthYear(Number(index), Number(year))
}

// Dezelfde afkapping als de verdeel-uitsplitsing op de factuurkaart
// (components/invoices/DistributionSummary.tsx): hetzelfde probleem, dezelfde getallen.
const MAX_ROWS = 5
const SHOWN_WHEN_TRUNCATED = 4

/**
 * Waar een maand uit is opgebouwd: elke verwachte factuur met klant, reeks, datum en
 * periode. Bij het eigen aandeel staat het deel voorop en het volle bedrag ernaast, want
 * dat eerste is het getal waar de staaf op sluit. Het totaal komt uit het aangeklikte
 * punt zelf en niet uit een hersommatie van de regels.
 */
function MonthLines({
  point,
  personal,
  expanded,
  onExpandedChange,
  onClose,
}: {
  point: ForecastPoint
  personal: boolean
  expanded: boolean
  onExpandedChange: (expanded: boolean) => void
  onClose: () => void
}) {
  const rows = point.lines
  const truncatable = rows.length > MAX_ROWS
  const collapsed = truncatable && !expanded
  const shown = collapsed ? rows.slice(0, SHOWN_WHEN_TRUNCATED) : rows
  const rest = rows.slice(shown.length)
  const restAmount = rest.reduce((sum, row) => sum + toNumber(row.share), 0)
  const scale = Math.max(1, ...shown.map((row) => toNumber(row.share)))
  // Bij een enkele regel draagt een staaf van 100% naast een totaal dat die regel
  // herhaalt geen enkele vergelijking; dan is de regel zelf het hele verhaal.
  const showBars = rows.length > 1

  return (
    <div className="animate-fade-in" role="group" aria-label={`Opbouw van ${monthLabel(point.month)}`}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="flex items-baseline gap-2">
          <span className="text-sm font-semibold text-ink">{monthLabel(point.month)}</span>
          <span className="text-xs text-ink-faint">{point.count} {point.count === 1 ? 'factuur' : 'facturen'}</span>
        </span>
        <span className="flex shrink-0 items-baseline gap-2">
          <span className="money text-sm font-semibold text-ink">{formatEuros(toNumber(point.amount))}</span>
          <Tooltip content="Terug naar de grafiek" tapToToggle={false}>
            <button type="button" onClick={onClose} aria-label="Terug naar de grafiek" className="btn-ghost h-8 w-8 shrink-0 p-0 text-ink-faint">
              <X className="h-4 w-4" />
            </button>
          </Tooltip>
        </span>
      </div>

      {rows.length === 0 ? (
        <EmptyState icon={CalendarX} title="Geen reeksen deze maand" className="mt-4 py-8" />
      ) : (
        <div className="mt-3 space-y-1">
          {shown.map((line, i) => (
            <ForecastLineRow
              key={`${line.templateId}-${line.issueDate}`}
              line={line}
              personal={personal}
              scale={scale}
              index={i}
              showBar={showBars}
            />
          ))}
          {/* Een knop die van label wisselt, geen twee die elkaar vervangen: dat laatste
              unmount het element onder de focus. De rest krijgt bewust geen staaf: die
              rangschikt de genoemde reeksen onderling, en een opgetelde groep is daar
              geen deelnemer van. */}
          {truncatable ? (
            <button
              type="button"
              onClick={() => onExpandedChange(!expanded)}
              aria-expanded={expanded}
              className="-mx-2 flex w-[calc(100%+1rem)] items-baseline justify-between gap-3 rounded-lg px-2 py-1 text-sm transition-colors hover:bg-fill-1 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-500/40"
            >
              <span className="inline-flex min-w-0 items-baseline gap-1 text-ink-faint">
                {expanded ? (
                  <ChevronUp className="h-3.5 w-3.5 shrink-0 translate-y-0.5" strokeWidth={1.85} />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5 shrink-0 translate-y-0.5" strokeWidth={1.85} />
                )}
                <span className="truncate">{collapsed ? `Overig (${rest.length} reeksen)` : 'Minder tonen'}</span>
              </span>
              {collapsed ? <span className="money shrink-0 font-medium text-ink">{formatEuros(restAmount)}</span> : null}
            </button>
          ) : null}
        </div>
      )}
    </div>
  )
}

/**
 * Een verwachte factuur, met de staaf in de `PersonBars`-grammatica: `accent-600/80` op
 * een `fill-2`-baan, geschaald op de grootste regel. De hele regel gaat naar de reeks.
 */
function ForecastLineRow({
  line,
  personal,
  scale,
  index,
  showBar,
}: {
  line: ForecastLine
  personal: boolean
  scale: number
  index: number
  showBar: boolean
}) {
  const share = toNumber(line.share)
  const full = toNumber(line.amount)

  return (
    <Link
      to="/terugkerend/$id"
      params={{ id: line.templateId }}
      className="-mx-2 block rounded-lg px-2 py-1 transition-colors hover:bg-fill-1 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-500/40"
    >
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="min-w-0 truncate text-ink-soft">{line.clientName} · {line.name}</span>
        <span className="flex shrink-0 items-baseline gap-2">
          {personal && share !== full ? (
            <span className="money text-[11px] leading-none text-ink-faint">van {formatEuros(full)}</span>
          ) : null}
          <span className="money font-medium text-ink">{formatEuros(share)}</span>
        </span>
      </div>
      <div className="mt-0.5 flex items-baseline justify-between gap-3 text-xs text-ink-faint">
        <span className="min-w-0 truncate">
          <span className="num">{line.issueDate}</span> · {line.periodLabel}
        </span>
        {line.indexed ? <span className="label-caps shrink-0">indexatie</span> : null}
      </div>
      {showBar && share > 0 ? (
        <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-fill-2">
          <div
            className="h-full origin-left animate-grow-bar-x bg-accent-600/80"
            style={{ width: `${(share / scale) * 100}%`, animationDelay: `${index * 60}ms` }}
          />
        </div>
      ) : null}
    </Link>
  )
}

function ClientCard({ bucket }: { bucket: ClientBucket }) {
  return (
    <div className="overflow-hidden rounded-card bg-surface shadow-card">
      <div className="flex items-center gap-2.5 border-b border-hairline px-5 py-3">
        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: bucket.brandColor }} />
        <span className="font-medium text-ink">{bucket.clientName}</span>
        <span className="text-sm text-ink-faint">
          {bucket.templates.length} {bucket.templates.length === 1 ? 'reeks' : 'reeksen'}
        </span>
      </div>
      <div className="divide-y divide-hairline">
        {bucket.templates.map((template) => (
          <RecurringRow key={template.id} template={template} />
        ))}
      </div>
    </div>
  )
}

function RecurringRow({ template }: { template: RecurringListItem }) {
  const isAdmin = useIsAdmin()
  const actions = useRecurringActions(template.id)
  const [menuOpen, setMenuOpen] = useState(false)

  return (
    <div className="grid grid-cols-[1fr_auto] items-center gap-3 px-5 py-3 sm:grid-cols-[1fr_7rem_8rem_6rem_7rem_5rem] sm:gap-4">
      <Link to="/terugkerend/$id" params={{ id: template.id }} className="flex min-w-0 items-center gap-2 text-left">
        <span className="min-w-0">
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="truncate font-medium text-ink">{template.name}</span>
            {template.openCommentCount > 0 ? (
              <span
                className="inline-flex shrink-0 items-center gap-1 rounded-full bg-fill-2 px-1.5 py-0.5 text-xs font-medium text-ink-soft"
                title={`${template.openCommentCount} open ${template.openCommentCount === 1 ? 'opmerking' : 'opmerkingen'}`}
              >
                <MessageSquare className="h-3 w-3" />
                {template.openCommentCount}
              </span>
            ) : null}
          </span>
          <span className="block truncate text-xs text-ink-faint sm:hidden">
            {FREQ_LABEL[template.frequency]}
            {template.intervalCount > 1 ? ` x${template.intervalCount}` : ''}
            {' · volgend '}{template.nextIssueDate ?? '-'}
          </span>
        </span>
      </Link>
      <span className="hidden text-sm text-ink-soft sm:block">
        {FREQ_LABEL[template.frequency]}
        {template.intervalCount > 1 ? ` x${template.intervalCount}` : ''}
      </span>
      <span className="num hidden text-sm text-ink-soft sm:block">{template.nextIssueDate ?? '-'}</span>
      <span className="hidden text-sm sm:block">
        {template.indexed ? (
          template.indexationPending ? (
            <span className="inline-flex items-center rounded-full bg-status-overdue/10 px-2 py-0.5 text-xs font-medium text-status-overdue">Open</span>
          ) : (
            <span className="text-ink-soft">Jaarlijks</span>
          )
        ) : (
          <span className="text-ink-faint">Geen</span>
        )}
      </span>
      <span className="num hidden text-sm text-ink-faint sm:block">{template.lastRunAt ?? '-'}</span>
      <div className="flex items-center justify-end gap-1.5">
        <span className={cn('h-2 w-2 rounded-full', template.active ? 'bg-status-paid' : 'bg-ink-faint/40')} />
        {isAdmin ? (
          <Popover.Root open={menuOpen} onOpenChange={setMenuOpen}>
            <Popover.Trigger asChild>
              <button type="button" className="btn-ghost h-8 w-8 p-0" aria-label="Acties">
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </Popover.Trigger>
            <Popover.Portal>
              <Popover.Content align="end" sideOffset={6} className="z-40 w-48 rounded-card border border-hairline bg-surface p-1.5 shadow-pop animate-fade-in">
                <button type="button" className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm text-ink hover:bg-fill-2" onClick={() => { setMenuOpen(false); actions.generateNow.mutate() }}>
                  <Sparkles className="h-4 w-4" /> Nu genereren
                </button>
                <button type="button" className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm text-ink hover:bg-fill-2" onClick={() => { setMenuOpen(false); actions.setActive.mutate(!template.active) }}>
                  {template.active ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                  {template.active ? 'Pauzeren' : 'Hervatten'}
                </button>
                <Link to="/terugkerend/$id" params={{ id: template.id }} className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm text-ink hover:bg-fill-2">
                  <Pencil className="h-4 w-4" /> Bewerken
                </Link>
              </Popover.Content>
            </Popover.Portal>
          </Popover.Root>
        ) : null}
      </div>
    </div>
  )
}

function CreateRecurringModal({ onClose }: { onClose: () => void }) {
  const clientLabelId = useId()
  const frequencyLabelId = useId()
  const navigate = useNavigate()
  const clientsQuery = useInvoiceClients()
  const createMutation = useCreateRecurring()

  const [clientId, setClientId] = useState('')
  const [name, setName] = useState('')
  const [subjectTemplate, setSubjectTemplate] = useState('')
  const [frequency, setFrequency] = useState<RecurringFrequency>('YEARLY')
  const [nextIssueDate, setNextIssueDate] = useState('')

  const clients = clientsQuery.data ?? []
  const canSubmit = clientId !== '' && name.trim() !== '' && subjectTemplate.trim() !== '' && nextIssueDate !== '' && !createMutation.isPending

  const submit = () =>
    createMutation.mutate(
      {
        clientId,
        name: name.trim(),
        subjectTemplate: subjectTemplate.trim(),
        nextIssueDate,
        frequency,
        groups: [{ heading: 'Werkzaamheden', distribution: [], lines: [] }],
      },
      {
        onSuccess: (detail) => {
          onClose()
          navigate({ to: '/terugkerend/$id', params: { id: detail.id } })
        },
      },
    )

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Nieuwe reeks"
      description="Het schema; groepen, regels en verdeling vul je hierna in."
      className="w-[min(94vw,560px)]"
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>Annuleren</button>
          <button type="button" className="btn-primary" onClick={submit} disabled={!canSubmit}>
            Reeks aanmaken
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <label className="block">
          <span id={clientLabelId} className="mb-1.5 block text-sm font-medium text-ink-soft">Klant</span>
          <SearchSelect
            value={clientId}
            onValueChange={setClientId}
            options={clients.map((client) => ({ value: client.id, label: client.name, lastActivityAt: client.lastActivityAt }))}
            placeholder={clientsQuery.isLoading ? 'Laden...' : 'Kies een klant'}
            searchPlaceholder="Zoek klant…"
            emptyLabel="Geen klant gevonden."
            disabled={clientsQuery.isLoading}
            ariaLabelledby={clientLabelId}
            className="w-full"
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Naam</span>
          <input className="field" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jaarlicentie platform" />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Onderwerp-sjabloon</span>
          <input className="field" value={subjectTemplate} onChange={(e) => setSubjectTemplate(e.target.value)} placeholder="Licentie {{jaar}}" />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span id={frequencyLabelId} className="mb-1.5 block text-sm font-medium text-ink-soft">Frequentie</span>
            <Select
              value={frequency}
              onValueChange={(v) => setFrequency(v as RecurringFrequency)}
              options={FREQ_OPTIONS.map((freq) => ({ value: freq, label: FREQ_LABEL[freq] }))}
              ariaLabelledby={frequencyLabelId}
              className="w-full"
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-ink-soft">Volgende factuur</span>
            <input className="field" type="date" value={nextIssueDate} onChange={(e) => setNextIssueDate(e.target.value)} />
          </label>
        </div>
      </div>
    </Modal>
  )
}
